import {
  collection,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  query,
  orderBy,
} from 'firebase/firestore';
import { db } from '../firebase.config';
import productsCatalog from '../assets/data/products';

/**
 * Fetches all orders across all customers from Cloud Firestore
 */
export const getAllOrders = async () => {
  try {
    const ordersRef = collection(db, 'orders');
    let q;
    try {
      q = query(ordersRef, orderBy('createdAt', 'desc'));
    } catch {
      q = ordersRef;
    }

    const snapshot = await getDocs(q);
    const orders = [];
    snapshot.forEach((d) => {
      orders.push({ id: d.id, ...d.data() });
    });

    // Sort descending by date in memory as fallback
    return orders.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  } catch (error) {
    console.error('Error fetching all orders in adminService:', error);
    throw error;
  }
};

/**
 * Updates the status and optional courier/tracking details of an order
 */
export const updateOrderStatus = async (orderId, newStatus, trackingInfo = {}) => {
  try {
    const orderDocRef = doc(db, 'orders', orderId);
    const payload = {
      status: newStatus,
      updatedAt: new Date().toISOString(),
      ...trackingInfo,
    };
    await updateDoc(orderDocRef, payload);
    return true;
  } catch (error) {
    console.error('Error updating order status in adminService:', error);
    throw error;
  }
};

/**
 * Bulk updates the status of multiple orders
 */
export const bulkUpdateOrderStatus = async (orderIds, newStatus) => {
  try {
    const promises = orderIds.map((id) => updateOrderStatus(id, newStatus));
    await Promise.all(promises);
    return true;
  } catch (error) {
    console.error('Error bulk updating order statuses:', error);
    throw error;
  }
};

/**
 * Deletes an order from Cloud Firestore (for admin housekeeping)
 */
export const deleteOrder = async (orderId) => {
  try {
    const orderDocRef = doc(db, 'orders', orderId);
    await deleteDoc(orderDocRef);
    return true;
  } catch (error) {
    console.error('Error deleting order in adminService:', error);
    throw error;
  }
};

/**
 * Fetches all registered users from Cloud Firestore
 */
export const getUsersList = async () => {
  try {
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    const users = [];
    snapshot.forEach((d) => {
      users.push({ id: d.id, ...d.data() });
    });
    return users.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  } catch (error) {
    console.error('Error fetching users in adminService:', error);
    return [];
  }
};

/**
 * Updates a user's role (e.g. 'admin' or 'customer') in Firestore
 */
export const updateUserRole = async (userId, newRole) => {
  try {
    const userDocRef = doc(db, 'users', userId);
    await updateDoc(userDocRef, {
      role: newRole,
      roleUpdatedAt: new Date().toISOString(),
    });
    return true;
  } catch (error) {
    console.error('Error updating user role in adminService:', error);
    throw error;
  }
};

// ==========================================
// Cloud Firestore + Local Cache Products Management
// ==========================================
const CUSTOM_PRODUCTS_KEY = 'lhabilleur_custom_products';

export const getCustomProducts = () => {
  try {
    const raw = localStorage.getItem(CUSTOM_PRODUCTS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const resolveAdminStock = (p) => {
  if (p.stockQuantity !== undefined && p.stockQuantity !== null && !isNaN(Number(p.stockQuantity))) {
    return Math.max(0, Number(p.stockQuantity));
  }
  if (p.stock !== undefined && p.stock !== null && !isNaN(Number(p.stock))) {
    return Math.max(0, Number(p.stock));
  }
  const seed = String(p.id || '1').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return ((seed * 13 + 7) % 25) + 4;
};

const mapProductWithStock = (p) => {
  const stockQty = resolveAdminStock(p);
  return {
    ...p,
    stockQuantity: stockQty,
    stock: stockQty,
    inStock: p.inStock !== false && stockQty > 0,
    price: Number(p.price) || 0,
  };
};

/**
 * Fetches all catalog products combining Firestore real-time collection + base catalog
 */
export const getAllProducts = async () => {
  try {
    const productsRef = collection(db, 'products');
    const snapshot = await getDocs(productsRef);
    const firestoreProducts = [];
    snapshot.forEach((d) => {
      firestoreProducts.push({ id: d.id, ...d.data() });
    });

    const localCustom = getCustomProducts();

    // Map by ID to ensure custom/edited versions take precedence over static catalog
    const productMap = new Map();

    // 1. Base catalog
    productsCatalog.forEach((p) => productMap.set(p.id, mapProductWithStock(p)));

    // 2. Local custom storage
    localCustom.forEach((p) => productMap.set(p.id, mapProductWithStock(p)));

    // 3. Cloud Firestore (highest precedence)
    firestoreProducts.forEach((p) => productMap.set(p.id, mapProductWithStock(p)));

    return Array.from(productMap.values());
  } catch (error) {
    console.warn('Could not fetch from Firestore products, using local fallback:', error);
    const localCustom = getCustomProducts();
    const map = new Map();
    productsCatalog.forEach((p) => map.set(p.id, mapProductWithStock(p)));
    localCustom.forEach((p) => map.set(p.id, mapProductWithStock(p)));
    return Array.from(map.values());
  }
};

/**
 * Saves a new product or updates an existing piece in Firestore & local cache
 */
export const saveProduct = async (productData) => {
  try {
    const id = productData.id || `piece-${Date.now().toString().slice(-6)}`;
    const stockQuantity =
      productData.stockQuantity !== undefined && productData.stockQuantity !== null && !isNaN(Number(productData.stockQuantity))
        ? Math.max(0, Number(productData.stockQuantity))
        : productData.stock !== undefined && productData.stock !== null && !isNaN(Number(productData.stock))
        ? Math.max(0, Number(productData.stock))
        : 15;

    const productWithMeta = {
      ...productData,
      id,
      price: Number(productData.price) || 0,
      stockQuantity,
      stock: stockQuantity,
      inStock: productData.inStock !== false && stockQuantity > 0,
      isCustom: true,
      updatedAt: new Date().toISOString(),
      createdAt: productData.createdAt || new Date().toISOString(),
    };

    // 1. Sync to Cloud Firestore
    try {
      const prodDoc = doc(db, 'products', id);
      await setDoc(prodDoc, productWithMeta, { merge: true });
    } catch (fsErr) {
      console.warn('Firestore product write skipped/failed, keeping local:', fsErr);
    }

    // 2. Cache in localStorage
    const current = getCustomProducts();
    const filtered = current.filter((p) => p.id !== id);
    const updated = [productWithMeta, ...filtered];
    localStorage.setItem(CUSTOM_PRODUCTS_KEY, JSON.stringify(updated));

    try {
      window.dispatchEvent(new Event('lhabilleur_products_updated'));
    } catch (e) {}

    return productWithMeta;
  } catch (error) {
    console.error('Error saving product in adminService:', error);
    throw error;
  }
};

/**
 * Deletes a product from Firestore & local cache
 */
export const deleteProduct = async (productId) => {
  try {
    // 1. Delete from Firestore
    try {
      const prodDoc = doc(db, 'products', productId);
      await deleteDoc(prodDoc);
    } catch (fsErr) {
      console.warn('Firestore product delete skipped/failed:', fsErr);
    }

    // 2. Delete from localStorage
    const current = getCustomProducts();
    const updated = current.filter((p) => p.id !== productId);
    localStorage.setItem(CUSTOM_PRODUCTS_KEY, JSON.stringify(updated));

    try {
      window.dispatchEvent(new Event('lhabilleur_products_updated'));
    } catch (e) {}

    return true;
  } catch (error) {
    console.error('Error deleting product in adminService:', error);
    throw error;
  }
};

// Backward-compatibility aliases
export const saveCustomProduct = saveProduct;
export const deleteCustomProduct = deleteProduct;

// ==========================================
// Atelier Storefront Global Settings
// ==========================================
const STORE_SETTINGS_KEY = 'lhabilleur_atelier_settings';
const DEFAULT_SETTINGS = {
  storeStatus: 'online', // 'online' | 'maintenance'
  whiteGloveDelivery: 'active', // 'active' | 'paused'
  automatedInvoicing: 'enabled',
  freeShippingThreshold: 500,
  announcementText: 'Complimentary White-Glove Delivery Across Egypt & Overseas Commissions',
};

export const getStoreSettings = async () => {
  try {
    // Try Firestore settings doc
    const settingsDoc = doc(db, 'settings', 'storefront');
    const snap = await getDoc(settingsDoc);
    if (snap.exists()) {
      return { ...DEFAULT_SETTINGS, ...snap.data() };
    }
  } catch {
    // fallback to localStorage
  }

  try {
    const raw = localStorage.getItem(STORE_SETTINGS_KEY);
    return raw ? { ...DEFAULT_SETTINGS, ...JSON.parse(raw) } : DEFAULT_SETTINGS;
  } catch {
    return DEFAULT_SETTINGS;
  }
};

export const updateStoreSettings = async (newSettings) => {
  try {
    const payload = {
      ...newSettings,
      updatedAt: new Date().toISOString(),
    };

    // 1. Write to Firestore
    try {
      const settingsDoc = doc(db, 'settings', 'storefront');
      await setDoc(settingsDoc, payload, { merge: true });
    } catch (fsErr) {
      console.warn('Firestore settings write skipped:', fsErr);
    }

    // 2. Cache in localStorage
    localStorage.setItem(STORE_SETTINGS_KEY, JSON.stringify(payload));
    return payload;
  } catch (error) {
    console.error('Error updating store settings:', error);
    throw error;
  }
};
